import { FC, useMemo } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { Security, useOktaAuth } from '@okta/okta-react';
import { SplunkNavigationLogger, SplunkProvider } from '@resolutionlife/ui';
import { ErrorBoundary } from '@sentry/react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { IconContext as Icon } from 'react-icons';
import { useNavigate } from 'react-router-dom';
import { AuthProvider, oktaAuth } from './auth';
import { AppRoutes } from './configs/routes';
import { useSplunkConfig } from './configs/splunk-config';

import { GraphQLProvider } from './providers/graphql';
import { globalTheme } from './styles/global';
import { FallbackError } from './ui/errors/fallback-error';

const BASE_PAGE_TITLE = 'ResRe';

/** Inner component that has access to Okta auth context for fetching Splunk config. */
const AuthenticatedApp: FC = () => {
  const { oktaAuth: auth } = useOktaAuth();
  const splunkConfig = useSplunkConfig(auth);

  return (
    <SplunkProvider config={splunkConfig}>
      <AuthProvider>
        <GraphQLProvider>
          <SplunkNavigationLogger />
          <AppRoutes />
        </GraphQLProvider>
      </AuthProvider>
    </SplunkProvider>
  );
};

export const App: FC = () => {
  const navigate = useNavigate();

  const onAuthRequired = () => {
    oktaAuth.signInWithRedirect();
  };

  const restoreOriginalUri = (_oktaAuth: any, originalUri: string) => {
    const isValidRedirectPath = (path: string | null): path is string => {
      if (!path || typeof path !== 'string' || path.length > 2048) return false;
      if (!path.startsWith('/')) return false;
      if (path.includes('://') || path.startsWith('//')) return false;
      if (path.includes('/login') || path.includes('/callback') || path.includes('/admin')) return false;
      if (path.includes('..') || path.includes('%2e%2e') || path.includes('\\')) return false;
      if (!/^[a-zA-Z0-9/_\-?&=.]+$/.test(path)) return false;
      return true;
    };
    
    // Priority: sessionStorage > originalUri > default
    const storedRedirect = sessionStorage.getItem('redirectPath');
    let targetUri = '/';
    
    if (isValidRedirectPath(storedRedirect)) {
      targetUri = storedRedirect;
      sessionStorage.removeItem('redirectPath');
    } else if (isValidRedirectPath(originalUri)) {
      targetUri = originalUri;
    }
    
    navigate(toRelativeUrl(targetUri, window.location.origin), { replace: true });
  };

  const iconContextValue = useMemo(() => ({ style: { verticalAlign: 'middle' } }), []);

  return (
    <ChakraProvider theme={globalTheme}>
      <HelmetProvider>
        <Helmet
          defaultTitle={BASE_PAGE_TITLE}
          titleTemplate={`${BASE_PAGE_TITLE} - %s`}
        />
        <Icon.Provider value={iconContextValue}>
          <ErrorBoundary fallback={<FallbackError />}>
            <Security
              oktaAuth={oktaAuth}
              onAuthRequired={onAuthRequired}
              restoreOriginalUri={restoreOriginalUri}
            >
              <AuthenticatedApp />
            </Security>
          </ErrorBoundary>
        </Icon.Provider>
      </HelmetProvider>
    </ChakraProvider>
  );
};
